import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import {
  CUSTOMIZATION_GROUP_LABELS,
  type CustomizationField,
  type CustomizationFieldType,
  type CustomizationGroup,
  type CustomizationSchema,
} from '@/types/activations';

interface SchemaBuilderProps {
  schema: CustomizationSchema;
  onChange: (schema: CustomizationSchema) => void;
}

const FIELD_TYPES: { value: CustomizationFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Toggle' },
  { value: 'select', label: 'Dropdown' },
  { value: 'color', label: 'Colour' },
  { value: 'multiline-list', label: 'List (one per line)' },
];

const GROUPS: CustomizationGroup[] = ['branding', 'copy', 'layout', 'settings'];

function slugifyKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Authors a template's CustomizationSchema: the declarative list of bounded editable fields
 * the activation runner / customization form will expose. This is the deliberate-limits surface
 * — Athlyn Team declares exactly which branding tokens, copy strings, layout options, and type
 * settings clients may change per event/activation.
 */
export function SchemaBuilder({ schema, onChange }: SchemaBuilderProps) {
  const fields = schema.fields ?? [];

  const update = (next: CustomizationField[]) => onChange({ ...schema, fields: next });

  const addField = () => {
    update([
      ...fields,
      {
        key: `field_${fields.length + 1}`,
        label: '',
        type: 'text',
        group: 'settings',
      },
    ]);
  };

  const patchField = (index: number, patch: Partial<CustomizationField>) => {
    update(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  const removeField = (index: number) => update(fields.filter((_, i) => i !== index));

  const moveField = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    [next[index], next[target]] = [next[target], next[index]];
    update(next);
  };

  return (
    <div className="space-y-4" data-testid="schema-builder">
      {fields.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="schema-builder-empty">
          No editable fields yet. Add a field to define what can be customised on this template.
        </p>
      )}

      {fields.map((field, index) => (
        <Card key={index} className="space-y-3 p-4" data-testid={`schema-builder-field-${index}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="grid flex-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor={`sb-label-${index}`}>Label</Label>
                <Input
                  id={`sb-label-${index}`}
                  data-testid={`sb-label-${index}`}
                  value={field.label}
                  placeholder="e.g. Prize name"
                  onChange={(e) => {
                    const label = e.target.value;
                    // Auto-derive the key from the label until the user edits it directly.
                    const autoKey = slugifyKey(field.label);
                    const keyShouldFollow = field.key === '' || field.key === autoKey || field.key === `field_${index + 1}`;
                    patchField(index, {
                      label,
                      ...(keyShouldFollow ? { key: slugifyKey(label) || `field_${index + 1}` } : {}),
                    });
                  }}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor={`sb-key-${index}`}>Config key</Label>
                <Input
                  id={`sb-key-${index}`}
                  data-testid={`sb-key-${index}`}
                  value={field.key}
                  placeholder="prize_name"
                  onChange={(e) => patchField(index, { key: slugifyKey(e.target.value) })}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor={`sb-type-${index}`}>Type</Label>
                <Select
                  value={field.type}
                  onValueChange={(v) => patchField(index, { type: v as CustomizationFieldType })}
                >
                  <SelectTrigger id={`sb-type-${index}`} data-testid={`sb-type-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor={`sb-group-${index}`}>Group</Label>
                <Select
                  value={field.group ?? 'settings'}
                  onValueChange={(v) => patchField(index, { group: v as CustomizationGroup })}
                >
                  <SelectTrigger id={`sb-group-${index}`} data-testid={`sb-group-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUPS.map((g) => (
                      <SelectItem key={g} value={g}>
                        {CUSTOMIZATION_GROUP_LABELS[g]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {field.type === 'select' && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`sb-options-${index}`}>
                    Options (one per line, <code>value|Label</code>)
                  </Label>
                  <Input
                    id={`sb-options-${index}`}
                    data-testid={`sb-options-${index}`}
                    value={(field.options ?? []).map((o) => `${o.value}|${o.label}`).join(', ')}
                    placeholder="prize|Prize Draw, leaderboard|Leaderboard"
                    onChange={(e) =>
                      patchField(index, {
                        options: e.target.value
                          .split(',')
                          .map((token) => token.trim())
                          .filter(Boolean)
                          .map((token) => {
                            const [value, label] = token.split('|');
                            return { value: value?.trim() ?? '', label: (label ?? value)?.trim() ?? '' };
                          }),
                      })
                    }
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Move field up"
                data-testid={`sb-up-${index}`}
                disabled={index === 0}
                onClick={() => moveField(index, -1)}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Move field down"
                data-testid={`sb-down-${index}`}
                disabled={index === fields.length - 1}
                onClick={() => moveField(index, 1)}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Remove field"
                data-testid={`sb-remove-${index}`}
                onClick={() => removeField(index)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        </Card>
      ))}

      <Button type="button" variant="outline" onClick={addField} data-testid="schema-builder-add">
        <Plus className="mr-2 h-4 w-4" />
        Add field
      </Button>
    </div>
  );
}
